import React, { useEffect } from "react";

import { Modal } from "@packages/ds";
import { Navigate } from "react-router-dom";

import Loading from "ds/PageLoading";
import useDecodedParams from "Hooks/useDecodedParams";
import useQueryParams from "Hooks/useQueryStrings";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import getUrl from "Libs/getUrl";
import {
  environmentLoadingSelector,
  isEnvironmentInactiveSelector
} from "Reducers/environment";
import {
  isLoadingSelector,
  deploymentStatusCodesSelector,
  deploymentSizingApiEnabledSelector
} from "Reducers/environment/deployment";
import { useAppSelector } from "Store/hooks";

import ConfigureResourcesPage from "./ConfigureResourcesPage";
import * as S from "./ConfigureResourcesPage.style";

const ConfigureResourcesPageNavigator = () => {
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const { previousPage } = useQueryParams(["previousPage"]);

  const isDeploymentLoading = useAppSelector(isLoadingSelector);
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector
  );
  const isEnvironmentInactive = useSelectorWithUrlParams(
    isEnvironmentInactiveSelector
  );
  const { current: currentCode, next: nextCode } = useSelectorWithUrlParams(
    deploymentStatusCodesSelector
  );
  const isSizingApiEnabled = useSelectorWithUrlParams(
    deploymentSizingApiEnabledSelector
  );
  const [environmentOverviewUrl] = useUrls([
    "organization.project.environment"
  ]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "scroll";
    };
  }, []);

  if (
    (typeof isSizingApiEnabled !== "undefined" && !isSizingApiEnabled) ||
    isEnvironmentInactive
  ) {
    return <Navigate to={environmentOverviewUrl} />;
  }

  if (!previousPage) {
    const environmentOverviewUrl = getUrl({
      key: "organization.project.environment",
      props: { organizationId, projectId, environmentId }
    });

    const urlWithPreviousPageQueryParam = getUrl({
      key: "organization.project.environment.configureResources",
      props: {
        organizationId,
        projectId,
        environmentId: environmentId || "",
        location: environmentOverviewUrl,
        queryParams: ""
      },
      noEncode: ["location"]
    });

    return <Navigate to={urlWithPreviousPageQueryParam} />;
  }

  const pageContent =
    isDeploymentLoading ||
    isEnvironmentLoading ||
    (typeof currentCode === "undefined" && typeof nextCode === "undefined") ? (
      <S.FormWrapper>
        <S.LoadingWrapper>
          <Loading />
        </S.LoadingWrapper>
      </S.FormWrapper>
    ) : (
      <ConfigureResourcesPage />
    );

  return (
    <Modal isOpen size="fullPage" modal={false} showCloseBtn={false}>
      {pageContent}
    </Modal>
  );
};

export default ConfigureResourcesPageNavigator;
